/* A Generic Modal Component
  Desktop: Displays a white modal on a dark background
  Mobile: Full-page white modal

  Props:
  -> children: content to display
  -> open: boolean, whether the modal is open or not
  -> close: function, called when modal close triggered

  The component body also accepts classnames as a prop and should
  work with styled components
*/

// TODO: Dry with base modal

import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { Portal } from 'react-portal';

import { Theme } from 'ui_system/theme';

const Wrapper = styled('div')`
  position: fixed;
  width: 100vw;
  height: 100vh;
  left: 0%;
  top: 0%;
  margin: 0 auto;
  background: rgba(0, 0, 0, 0.5);
  visibility: ${(props) => (props.open ? 'visible' : 'hidden')};
  z-index: ${(props) => props.zIndex || 100};
`;

const fullScreenMobileBody = css`
  width: 100vw;
  height: 100vh;
  max-height: 100vh;
  overflow-x: scroll;
  padding: 25px;
`;

const maxHeightMobileBody = css`
  overflow: visible !important;
  max-width: 310px;
  padding: 25px;
  height: auto;
`;

const ModalBody = styled('div')`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  margin: auto;

  width: ${(props) => (props.width ? `${props.width}px` : '600px')};
  height: ${(props) =>
    props.maxHeight ? `${props.maxHeight}px` : 'fit-content'};
  max-width: 100vw;
  max-height: ${(props) =>
    props.maxHeight ? `${props.maxHeight}px` : 'calc(100vh - 5%)'};

  background: white;

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    ${(props) =>
      props.mobileMaxHeight && `max-height: ${props.mobileMaxHeight}px;`}
    ${(props) =>
      props.mobileMaxHeight ? maxHeightMobileBody : fullScreenMobileBody}
  }

  /* General Transition rules */
  -webkit-transition: all ${(props) => (props.open ? '300ms' : '250ms')}
    cubic-bezier(0.465, 0.183, 0.153, 0.946);
  -moz-transition: all ${(props) => (props.open ? '300ms' : '250ms')}
    cubic-bezier(0.465, 0.183, 0.153, 0.946);
  transition: all ${(props) => (props.open ? '300ms' : '250ms')}
    cubic-bezier(0.465, 0.183, 0.153, 0.946);

  /* Scale in */
  transform: ${(props) => (props.open ? 'scale(1)' : 'scale(0.7)')};
  -webkit-transform: ${(props) => (props.open ? 'scale(1)' : 'scale(0.7)')};
  -moz-transform: ${(props) => (props.open ? 'scale(1)' : 'scale(0.7)')};
  -ms-transform: ${(props) => (props.open ? 'scale(1)' : 'scale(0.7)')};

  /* Fade in */
  opacity: ${(props) => (props.open ? 1 : 0)};

  will-change: transform, opacity;
`;

const fullScreenMobileClose = css`
  top: 20px;
  right: 20px;
  background: white;
  color: #999;
`;

const maxHeightMobileClose = css`
  top: -15px;
  right: -15px;
  width: 32px;
  height: 32px;
`;

const CloseButton = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;

  top: -20px;
  right: -20px;
  height: 40px;
  width: 40px;

  padding-top: 5px;
  border: 1px solid white;
  border-radius: 50%;
  background: #333;
  color: white;
  font-size: 16px;
  cursor: pointer;

  /* Mobile close button is inside of the full-page modal */
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    ${(props) =>
      props.mobileMaxHeight ? maxHeightMobileClose : fullScreenMobileClose}
  }
`;

const SimpleModal = (props) => {
  const {
    close,
    className,
    open,
    children,
    hideClose,
    preventClickOff,
    maxHeight,
    width,
    mobileMaxHeight,
    zIndex,
  } = props;

  return (
    <Portal>
      <Wrapper
        zIndex={zIndex}
        open={open}
        onClick={!preventClickOff ? close : undefined}
      >
        <ModalBody
          maxHeight={maxHeight}
          width={width}
          className={className}
          open={open}
          onClick={(e) => e.stopPropagation()}
          mobileMaxHeight={mobileMaxHeight}
        >
          {hideClose ? null : (
            <CloseButton
              open={open}
              onClick={close}
              mobileMaxHeight={mobileMaxHeight}
            >
              <i className="af-close" data-testid="close-simple-modal" />
            </CloseButton>
          )}
          {children}
        </ModalBody>
      </Wrapper>
    </Portal>
  );
};

SimpleModal.defaultProps = {
  hideClose: false,
  preventClickOff: false,
};

SimpleModal.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  close: PropTypes.func,
  hideClose: PropTypes.bool,
  maxHeight: PropTypes.number,
  mobileMaxHeight: PropTypes.number,
  open: PropTypes.bool,
  preventClickOff: PropTypes.bool,
  width: PropTypes.number,
};

export default SimpleModal;
